import { Trans, t } from "@lingui/macro";
import { Box, Container, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useWorkoutProfilesContext } from "@/hooks/useWorkoutProfilesContext";
import { BlueSwitch, MenuList, MenuListItemVariant } from "@WahooFitness/redesignr";
import useAssociatedDevices from "@/hooks/useAssociatedDevices";
import useFlaggedFeatures from "@/hooks/useFlaggedFeatures";
import { FlaggedFeature } from "@/hooks/types/FlaggedFeature";
import { ErrorBoundary, GlobalHeader } from "@WahooFitness/wahoo-offline-mfe";

const PlannedWorkoutSettings = ({ workoutProfileId }: { workoutProfileId: string }) => {
  const { hasAssociatedAce } = useAssociatedDevices();

  const {
    getWorkoutProfile,
    workoutProfilesLoading,
    workoutProfilesError,
    updateProfile,
    profileUpdateInProgress,
  } = useWorkoutProfilesContext();

  const workoutProfile = useMemo(
    () => getWorkoutProfile(workoutProfileId),
    [getWorkoutProfile, workoutProfileId]
  );

  const handleChangeAlertsPlansUseVoice = useCallback(
    (_: any, value: boolean) => {
      if (value !== null && value !== undefined && workoutProfile) {
        updateProfile(workoutProfile.external_id, "alerts_plans_use_voice", value);
      }
    },
    [updateProfile, workoutProfile]
  );

  const { checkIsFeatureEnabled, featuresAreLoading } = useFlaggedFeatures();

  const elemntPlannedWorkoutsEnabled = useMemo(
    () => checkIsFeatureEnabled(FlaggedFeature.ElemntPlannedWorkouts),
    [checkIsFeatureEnabled]
  );

  const sections = useMemo(
    () => [
      {
        id: "plans_alerts_on_other_pages",
        content: t`Alerts on other pages`,
        secondaryContent: t`Keeps you updated on your planned workout progress while not on planned workout page.`,
        variant: MenuListItemVariant.Action,
        disabled: profileUpdateInProgress,
        actionComponent: (
          <BlueSwitch
            disabled={profileUpdateInProgress}
            checked={workoutProfile?.plans_alerts_on_other_pages}
            data-testid="plans_alerts_on_other_pages"
          />
        ),
        action: () => {
          if (workoutProfile) {
            updateProfile(
              workoutProfile.external_id,
              "plans_alerts_on_other_pages",
              !workoutProfile.plans_alerts_on_other_pages
            );
          }
        },
      },
      {
        id: "alerts_plans",
        content: t`Play sound for new intervals`,
        secondaryContent:
          hasAssociatedAce && elemntPlannedWorkoutsEnabled ? (
            <Box display="flex" flexDirection="column" rowGap={1}>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={workoutProfile?.alerts_plans_use_voice}
                onChange={handleChangeAlertsPlansUseVoice}
                disabled={profileUpdateInProgress}
              >
                <ToggleButton value={true}>
                  <Trans>Voice</Trans>
                </ToggleButton>
                <ToggleButton value={false}>
                  <Trans>Beeps</Trans>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          ) : (
            <></>
          ),
        variant: MenuListItemVariant.Accordion,
        expanded: hasAssociatedAce && elemntPlannedWorkoutsEnabled && workoutProfile?.alerts_plans,
        disabled: profileUpdateInProgress,
        actionComponent: (
          <BlueSwitch
            disabled={profileUpdateInProgress}
            checked={workoutProfile?.alerts_plans}
            data-testid="alerts_plans"
          />
        ),
        action: () => {
          if (workoutProfile) {
            updateProfile(workoutProfile.external_id, "alerts_plans", !workoutProfile.alerts_plans);
          }
        },
      },
      {
        id: "plans_auto_lap_on_interval",
        content: t`Auto lap on interval`,
        secondaryContent: t`Trigger a new lap every interval.`,
        variant: MenuListItemVariant.Action,
        disabled: profileUpdateInProgress,
        actionComponent: (
          <BlueSwitch
            disabled={profileUpdateInProgress}
            checked={workoutProfile?.plans_auto_lap_on_interval}
            data-testid="plans_auto_lap_on_interval"
          />
        ),
        action: () => {
          if (workoutProfile) {
            updateProfile(
              workoutProfile.external_id,
              "plans_auto_lap_on_interval",
              !workoutProfile.plans_auto_lap_on_interval
            );
          }
        },
      },
      {
        id: "custom_alert_during_plan",
        content: t`Play custom alerts`,
        secondaryContent: t`Continue to get your custom alerts during the workout.`,
        variant: MenuListItemVariant.Action,
        disabled: profileUpdateInProgress,
        actionComponent: (
          <BlueSwitch
            disabled={profileUpdateInProgress}
            checked={workoutProfile?.custom_alert_during_plan}
            data-testid="custom_alert_during_plan"
          />
        ),
        action: () => {
          if (workoutProfile) {
            updateProfile(
              workoutProfile.external_id,
              "custom_alert_during_plan",
              !workoutProfile.custom_alert_during_plan
            );
          }
        },
      },
    ],
    [
      elemntPlannedWorkoutsEnabled,
      handleChangeAlertsPlansUseVoice,
      hasAssociatedAce,
      profileUpdateInProgress,
      updateProfile,
      workoutProfile,
    ]
  );

  return (
    <>
      <GlobalHeader title={t`Planned workouts`} />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <ErrorBoundary
          isLoading={workoutProfilesLoading || featuresAreLoading}
          error={workoutProfilesError}
        >
          <Box py={1}>
            <MenuList listItems={sections} />
          </Box>
        </ErrorBoundary>
      </Container>
    </>
  );
};

export default PlannedWorkoutSettings;
